import React, { useState } from 'react';
import { updateFeatureGroup } from '../services/featureService';
import './FeatureMapInfo.css';

const FeatureMapInfo = ({ featureGroupId, featureGroupName, featureGroupDescription, onUpdate }) => {
  const [displayName, setDisplayName] = useState(featureGroupName);
  const [description, setDescription] = useState(featureGroupDescription || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    setDisplayName(featureGroupName);
    setDescription(featureGroupDescription || '');
    setError(null);
    setSuccess(false);
  }, [featureGroupName, featureGroupDescription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (displayName.trim() === featureGroupName && description.trim() === (featureGroupDescription || '')) {
      setError('No changes to save');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      await updateFeatureGroup(featureGroupId, displayName.trim(), description.trim() || null);
      
      setSuccess(true);
      if (onUpdate) {
        onUpdate();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update feature map name');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feature-map-info">
      <div className="panel-header">
        <h3>Feature Map Info</h3>
        <p className="panel-description">Manage your feature map settings</p>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label htmlFor="displayName">
            Feature Map Name <span className="required">*</span>
          </label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setError(null);
              setSuccess(false);
            }}
            className={error ? 'error' : ''}
            placeholder="Enter feature map name"
            disabled={loading}
          />
          {error && <span className="error-message">{error}</span>}
          {success && (
            <span className="success-message">
              <i className="fas fa-check-circle"></i> Feature map name updated successfully!
            </span>
          )}
          <small className="help-text">
            This name will be displayed in the feature map selector
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Feature Map Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(false);
            }}
            placeholder="Enter feature map description"
            disabled={loading}
            rows={4}
          />
          <small className="help-text">
            Description of this feature map (used in prompt context)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="featureGroupId">
            Feature Map ID
          </label>
          <div className="id-field-container">
            <input
              id="featureGroupId"
              type="text"
              value={featureGroupId}
              readOnly
              className="id-field-readonly"
            />
            <button
              type="button"
              className="copy-id-button"
              onClick={() => {
                navigator.clipboard.writeText(featureGroupId).then(() => {
                  // Show temporary feedback
                  const button = document.querySelector('.copy-id-button');
                  if (button) {
                    const originalHTML = button.innerHTML;
                    button.innerHTML = '<i class="fas fa-check"></i>';
                    button.style.color = '#4CAF50';
                    setTimeout(() => {
                      button.innerHTML = originalHTML;
                      button.style.color = '';
                    }, 2000);
                  }
                }).catch(err => {
                  console.error('Failed to copy ID:', err);
                });
              }}
              title="Copy Feature Map ID to clipboard"
            >
              <i className="fas fa-copy"></i>
            </button>
          </div>
          <small className="help-text">
            Unique identifier for this feature map
          </small>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="save-button"
            disabled={loading || (displayName.trim() === featureGroupName && description.trim() === (featureGroupDescription || ''))}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeatureMapInfo;
